import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import messages from '@/features/translations/en-GB.json';

const COMPONENT_KEY_PREFIX = 'intl-msg:vehicle-diagnostics.maintenance.component.';

export interface MaintenanceComponentOption {
    /** Underscore-cased slug as stored in the DB / sent in the request (e.g. "air_filter"). */
    slug: string;
    /** Already-translated display label (e.g. "Air filter"), resolved via react-intl. */
    label: string;
}

/**
 * Returns the full, stable set of known maintenance components derived from the bundled
 * translation keys. The list is computed once (the JSON import is inlined at build time)
 * and memoized for reference stability. Labels are resolved through react-intl so the
 * active locale is respected.
 */
export const useMaintenanceComponentOptions = (): MaintenanceComponentOption[] => {
    const intl = useIntl();

    return useMemo(() => {
        const options = Object.keys(messages)
            .filter((key) => key.startsWith(COMPONENT_KEY_PREFIX))
            .map((key) => {
                const hyphenSlug = key.slice(COMPONENT_KEY_PREFIX.length);
                const slug = hyphenSlug.replaceAll('-', '_');
                const label = intl.formatMessage({ id: key, defaultMessage: hyphenSlug });
                return { slug, label };
            });
        return options.sort((a, b) => a.label.localeCompare(b.label));
    }, [intl]);
};
